import { useQuery } from '@apollo/client';
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Input, Icon, Loading } from 'design-system-web';
import { convertToUUID } from 'uuid-encoding';
import { GetBuildingTenantChannelsQuery } from 'graphql-query-contracts';
import { Channel } from 'application-types';
import { getBuildingTenantChannels } from 'graphql-queries';
import { useVantifySiteTenants } from 'integrations-vantify-react-hooks';
import type { SiteTenant } from 'integrations-vantify-client-services';
import styles from './VantifyTenantMapping.scss';
import type {
  VantifySettings,
  VantifyTenantMappingData,
} from '../../types/VantifyEditorTypes';
import { ToggleView } from '../ToggleView/ToggleView';
import { Alert, AlertType } from '../Alert/Alert';

interface VantifyTenantMappingProps {
  label?: string;
  onChange: (value: VantifyTenantMappingData[]) => void;
  channel: Channel;
  settings: VantifySettings;
  metadataLoading?: boolean;
  forCreate?: boolean;
}

interface BuildingTenant {
  label: string;
  value: string;
  vantifyTenantId?: number | null;
}
interface ContactInformationField {
  type: string;
  value: string;
}

interface ContactInformation {
  fields?: ContactInformationField[];
}
interface SiteTenantWithContactInfo extends SiteTenant {
  contactInformation?: ContactInformation;
}

interface VantifyTenantOption {
  label: string;
  value: number;
  email?: string;
  name?: string;
}

const getEmailFromContactInfo = (tenant: SiteTenantWithContactInfo): string => {
  const emailField = tenant.contactInformation?.fields?.find(
    (field) => field.type === 'email'
  );

  return emailField?.value || '';
};

const getTenantIdFromMapping = (
  mapping: VantifyTenantMappingData | undefined
): number | null => {
  if (!mapping) {
    return null;
  }

  return mapping.vantifyTenantId;
};

export const VantifyTenantMapping: React.FC<VantifyTenantMappingProps> = ({
  label,
  onChange,
  channel,
  settings,
  metadataLoading,
}) => {
  const { t } = useTranslation();
  const { data: buildingTenants } = useQuery<GetBuildingTenantChannelsQuery>(
    getBuildingTenantChannels,
    {
      variables: { channelId: convertToUUID(channel._id) },
    }
  );
  const { fetchSiteTenants, siteTenants, loading: siteTenantsLoading, error: siteTenantsError } = useVantifySiteTenants();
  const [buildingTenantList, setBuildingTenantList] = useState<BuildingTenant[]>([]);
  const [vantifyTenantList, setVantifyTenantList] = useState<VantifyTenantOption[]>([]);
  const [duplicateError, setDuplicateError] = useState<{vtsTenantId: string; vantifyTenantId: number;} | null>(null);
  const hasFetchedForSavedConfigRef = useRef(false);
  const prevMetadataLoadingRef = useRef<boolean | undefined>(undefined);
  const lastFetchedParamsRef = useRef<{ userId: number | undefined; siteId: number | undefined } | null>(null);
  const fetchSiteTenantsRef = useRef(fetchSiteTenants);

  const userId = useMemo(() => settings?.userId, [settings?.userId]);
  const siteId = useMemo((): number | undefined => {
    const id = settings?.siteId || (typeof settings?.site === 'object' ? settings.site?.value : null);
    
    if (id == null) {
      return undefined;
    }

    const numericId = typeof id === 'number' ? id : Number(id);

    return Number.isNaN(numericId) ? undefined : numericId;
  }, [settings?.siteId, settings?.site]);

  // Keep fetchSiteTenants ref up to date to avoid stale closures
  useEffect(() => {
    fetchSiteTenantsRef.current = fetchSiteTenants;
  }, [fetchSiteTenants]);

  // Track if we've done initial fetch for saved config (edit mode)
  const hasFetchedInitialRef = useRef(false);

  // Handle initial mount: fetch site tenants if userId and siteId are available (edit mode)
  // Note: This runs once on mount. In edit mode, userId/siteId are always available and metadataLoading is always false.
  useEffect(() => {
    if (!hasFetchedInitialRef.current && userId && siteId && !metadataLoading) {
      hasFetchedInitialRef.current = true;
      hasFetchedForSavedConfigRef.current = true;
      lastFetchedParamsRef.current = { userId, siteId };
      fetchSiteTenantsRef.current({
        userId,
        siteId,
      });
    }
  }, []);

  // Handle "load settings" button click: fetch when metadata loading completes
  useEffect(() => {
    if (prevMetadataLoadingRef.current === undefined) {
      prevMetadataLoadingRef.current = metadataLoading;

      return;
    }

    const loadSettingsJustCompleted = prevMetadataLoadingRef.current === true && !metadataLoading;

    if (loadSettingsJustCompleted && userId && siteId) {
      // Mark that we've fetched so the initial fetch logic doesn't run again
      hasFetchedInitialRef.current = true;
      lastFetchedParamsRef.current = { userId, siteId };
      fetchSiteTenantsRef.current({
        userId,
        siteId,
      });
    } else if (!metadataLoading && (!userId || !siteId)) {
      setVantifyTenantList([]);
    }

    prevMetadataLoadingRef.current = metadataLoading;
  }, [metadataLoading]);

  useEffect(() => {
    if (siteTenants && Array.isArray(siteTenants.contacts)) {
      const mappedTenants = siteTenants.contacts.map((contact: SiteTenantWithContactInfo) => ({
        label: contact.company || '',
        value: contact.id,
        email: getEmailFromContactInfo(contact),
        name: contact.name || '',
      }));

      setVantifyTenantList(mappedTenants);
    } else {
      setVantifyTenantList([]);
    }
  }, [siteTenants]);

  const tenantMappingsArray = useMemo(() => {
    return Array.isArray(settings?.tenantMappings) ? settings.tenantMappings : [];
  }, [settings?.tenantMappings]);

  const tenantMappingsString = useMemo(() => {
    return JSON.stringify(tenantMappingsArray);
  }, [tenantMappingsArray]);

  const prevTenantMappingsStringRef = useRef<string>('');
  const prevBuildingTenantsRef = useRef(buildingTenants?.getBuildingTenantChannels);

  useEffect(() => {
    if (!buildingTenants?.getBuildingTenantChannels) {
      return;
    }

    const buildingTenantsChanged = prevBuildingTenantsRef.current !== buildingTenants.getBuildingTenantChannels;
    const mappingsChanged = prevTenantMappingsStringRef.current !== tenantMappingsString;

    if (!buildingTenantsChanged && !mappingsChanged) {
      return;
    }

    const mappedTenants = buildingTenants.getBuildingTenantChannels
      .filter(tenant => tenant !== null)
      .map(tenant => {
        const tenantChannelLongUUID = convertToUUID(tenant.channelId);
        const mapping = tenantMappingsArray.find(
          m => m.vtsTenantId === tenantChannelLongUUID
        );

        return {
          label: tenant.name || '',
          value: tenant.channelId,
          vantifyTenantId: getTenantIdFromMapping(mapping),
        };
      });

    prevTenantMappingsStringRef.current = tenantMappingsString;
    prevBuildingTenantsRef.current = buildingTenants.getBuildingTenantChannels;
    setBuildingTenantList(mappedTenants);
  }, [buildingTenants?.getBuildingTenantChannels, tenantMappingsString, tenantMappingsArray]);

  const handleChange = useCallback((
    channelId: string,
    selectedTenantId: number | null
  ) => {
    const existingMappings = Array.isArray(settings?.tenantMappings) 
      ? settings.tenantMappings 
      : [];
    const tenantChannelLongUUID = convertToUUID(channelId);
    const existingMapping = existingMappings.find(
      m => m.vtsTenantId === tenantChannelLongUUID
    );

    if (selectedTenantId === null) {
      if (!existingMapping) {
        return;
      }

      setDuplicateError(null);

      const updatedMappings = existingMappings.filter(
        m => m.vtsTenantId !== tenantChannelLongUUID
      );

      onChange(updatedMappings);
      
      return;
    }

    if (existingMapping?.vantifyTenantId === selectedTenantId) {
      return;
    }

    const selectedTenant = vantifyTenantList.find(
      tenant => tenant.value === selectedTenantId
    );

    if (selectedTenant) {
      const alreadyMapped = existingMappings.find(
        m => m.vtsTenantId !== tenantChannelLongUUID && m.vantifyTenantId === selectedTenant.value
      );

      if (alreadyMapped) {
        setDuplicateError({
          vtsTenantId: tenantChannelLongUUID,
          vantifyTenantId: selectedTenant.value,
        });

        return;
      }

      setDuplicateError(null);

      const mapping: VantifyTenantMappingData = {
        vtsTenantId: tenantChannelLongUUID,
        vantifyTenantName: selectedTenant.name || '',
        vantifyTenantId: selectedTenant.value,
        vantifyTenantEmail: selectedTenant.email || '',
        vantifyTenantCompany: selectedTenant.label || '',
      };

      const updatedMappings = [
        ...existingMappings.filter(m => m.vtsTenantId !== tenantChannelLongUUID),
        mapping,
      ];

      onChange(updatedMappings);
    }
  }, [vantifyTenantList, settings?.tenantMappings, onChange]);

  const mappedCount = useMemo(
    () => buildingTenantList.filter(item => item.vantifyTenantId).length,
    [buildingTenantList]
  );

  const renderCollapseView = useCallback(() => {
    const totalCount = buildingTenantList.length;

    return (
      <div className={styles.collapseView}>
        <span>
          {t(
            'web.admin.channel.integrations.vantify.settings.mapped-count',
            {
              mapped: mappedCount,
              total: totalCount,
            }
          )}
        </span>
      </div>
    );
  }, [buildingTenantList, mappedCount, t]);

  const renderTenantMappingContent = useCallback(() => {
    if (siteTenantsLoading.fetchSiteTenants) {
      return (
        <div className={styles.loading}>
          <Loading testId="site-tenants-loading" />
        </div>
      );
    }

    if (siteTenantsError?.fetchSiteTenants) {
      return (
        <div className={styles.errorMessage}>
          {siteTenantsError.fetchSiteTenants}
        </div>
      );
    }

    const shouldShowWarning = 
      siteId !== undefined &&
      !siteTenantsLoading.fetchSiteTenants && 
      !siteTenantsError?.fetchSiteTenants && 
      siteTenants !== null &&
      siteTenants !== undefined && 
      vantifyTenantList.length === 0;

    return (
      <>
        {shouldShowWarning && (
          <Alert type={AlertType.warning} fullWidth>
            <div>
              <div className={styles.alertHeading}>
                {t('web.admin.channel.integrations.vantify.settings.no-site-tenants-available-heading')}
              </div>
              <div>
                {t('web.admin.channel.integrations.vantify.settings.no-site-tenants-available-subheading')}
              </div>
            </div>
          </Alert>
        )}
        {buildingTenantList.map((item, index) => {
          const convertedItemValue = convertToUUID(item.value);
          const hasError = duplicateError?.vtsTenantId === convertedItemValue;

          return (
            <div className={styles.flexItem} key={item.value}>
              <div className={styles.mappedContent}>
                <div className={styles.dropdownLabel}>
                  {t(
                    'web.admin.channel.integrations.vantify.settings.map-tenant'
                  )}
                </div>
                <Input
                  disabled
                  value={item.label}
                  onChange={() => null}
                  showClear={false}
                  fieldName={`tenant-${index}`}
                  inputClassName={styles.disabledInput}
                />
              </div>
              <Icon
                name="arrow-right"
                className={styles.rightArrow}
                testId="right-arrow-icon"
              />
              <div className={styles.mappedContent}>
                <div className={styles.dropdownLabel}>
                  {t(
                    'web.admin.channel.integrations.vantify.settings.map-with'
                  )}
                </div>
                <Dropdown
                  key={`${item.value}-${hasError}`}
                  items={vantifyTenantList}
                  value={item.vantifyTenantId ? Number(item.vantifyTenantId) : null}
                  onChange={selectedItem => {
                    handleChange(
                      item.value,
                      selectedItem ? selectedItem.value : null
                    );
                  }}
                  disabled={vantifyTenantList.length === 0}
                  invalid={hasError}
                  isFullWidth
                  isClearable
                  placeholder={
                    vantifyTenantList.length === 0
                      ? ('web.admin.channel.integrations.vantify.settings.no-tenants-available')
                      : ('web.admin.channel.integrations.vantify.settings.not-mapped')
                  }
                  testId={`vantify-tenant-${index}`}
                />
                {hasError ? (
                  <div className={styles.errorMessage}>
                    {t('web.admin.channel.integrations.vantify.settings.tenant-already-mapped')}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </>
    );
  }, [
    siteTenantsLoading.fetchSiteTenants,
    siteTenantsError?.fetchSiteTenants,
    buildingTenantList,
    duplicateError,
    vantifyTenantList,
    handleChange,
    t,
    siteTenants,
  ]);

  return (
    <div className={styles.tenantMapping}>
      <div className={styles.mappingBox}>
        <ToggleView
          testId="VantifyTenantMapping"
          title={<div className={styles.parentTitle}>{label}</div>}
          collapsedViewContent={renderCollapseView()}
          defaultExpanded
          className={styles.customToggle}
        >
          <div className={styles.parentDescription}>
            {t(
              'web.admin.channel.integrations.vantify.settings.tenant-mapping-subtitle'
            )}
          </div>
          {renderTenantMappingContent()}
        </ToggleView>
      </div>
    </div>
  );
};
